import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { assignmentService } from '../../services/assignmentService';
import { dailyRecordService } from '../../services/dailyRecordService';
import { companyService } from '../../services/companyService';
import { dailyTaskService } from '../../services/taskService';
import { toolboxTalksService } from '../../services/toolboxTalksService';
import { workerScheduleService } from '../../services/workerScheduleService';
import { sanitizeError } from '../../utils/sanitizeError';
import TaskDetailModal from '../../components/TaskDetailModal';
import SiteReadOnlyBanner from '../../components/SiteReadOnlyBanner';
import SubscriptionReadOnlyBanner from '../../components/SubscriptionReadOnlyBanner';
import useSiteReadOnly from '../../hooks/useSiteReadOnly';

const WorkerHome = () => {
  const navigate = useNavigate();
  const { user, userProfile } = useAuth();
  const { t } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [todayAssignment, setTodayAssignment] = useState(null);
  const [dailyRecordId, setDailyRecordId] = useState(null);
  const [todayTasks, setTodayTasks] = useState([]);
  const [upcomingAssignments, setUpcomingAssignments] = useState([]);
  const [greeting, setGreeting] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [error, setError] = useState('');
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [companySettings, setCompanySettings] = useState({
    require_toolbox_talk_signoff: false,
    enable_incident_reporting: true,
  });
  const [pendingToolboxTalks, setPendingToolboxTalks] = useState(0);
  const [detailTask, setDetailTask] = useState(null);
  const [toolboxBlocked, setToolboxBlocked] = useState(false);
  const [othersOnSite, setOthersOnSite] = useState([]);

  const { status: assignedSiteStatus, isReadOnly: assignedSiteReadOnly } = useSiteReadOnly(todayAssignment?.site_id);

  useEffect(() => {
    if (userProfile?.role !== 'worker') {
      // Route non-workers to their correct dashboard based on actual role
      const roleRoutes = {
        admin: '/admin-dashboard',
        site_manager: '/site-manager-dashboard',
        client: '/client-dashboard',
      };
      const destination = roleRoutes?.[userProfile?.role] || '/admin-dashboard';
      navigate(destination, { replace: true });
      return;
    }
    loadWorkerHome();
  }, [userProfile, navigate]);

  useEffect(() => {
    if (!user?.id) return;
    const key = `workerOnboardingSeen_${user?.id}`;
    if (!localStorage.getItem(key)) {
      setShowOnboarding(true);
    }
  }, [user?.id]);

  useEffect(() => {
    const hour = new Date()?.getHours();
    const firstName = userProfile?.full_name?.split(' ')?.[0] || 'there';
    if (hour < 12) setGreeting(`Good morning, ${firstName}`);
    else if (hour < 18) setGreeting(`Good afternoon, ${firstName}`);
    else setGreeting(`Good evening, ${firstName}`);
  }, [userProfile]);

  const loadWorkerHome = async () => {
    try {
      setLoading(true);
      setError('');

      const assignmentResult = await assignmentService?.getTodayAssignment(user?.id);
      if (assignmentResult?.error) throw assignmentResult?.error;
      setTodayAssignment(assignmentResult?.data);

      // Load other workers + the site manager scheduled on the same site today
      // so the worker can see who they're sharing the site with. Non-blocking:
      // a failure here just hides the "with…" line, doesn't break the page.
      if (assignmentResult?.data?.site_id) {
        const othersRes = await assignmentService?.getOthersOnSiteToday(
          assignmentResult.data.site_id,
          user?.id
        );
        if (othersRes?.error) {
          console.error('[WorkerHome] others-on-site failed:', othersRes.error);
        } else {
          setOthersOnSite(othersRes?.data || []);
        }
      }

      // Load today's tasks for this worker
      const today = new Date()?.toISOString()?.split('T')?.[0];
      const tasksResult = await dailyTaskService?.getForWorkerToday(user?.id, today);
      if (!tasksResult?.error) {
        setTodayTasks(tasksResult?.data || []);
      }

      // Load upcoming assignments (today + next 6 days)
      const upcomingResult = await workerScheduleService?.getWorkerUpcomingAssignments(user?.id, 6);
      if (!upcomingResult?.error) {
        setUpcomingAssignments(upcomingResult?.data || []);
      }

      if (assignmentResult?.data) {
        const companyResult = await companyService?.getUserCompany();
        if (companyResult?.error) throw companyResult?.error;

        // Load company settings
        if (companyResult?.data) {
          const settings = {
            require_toolbox_talk_signoff: companyResult?.data?.require_toolbox_talk_signoff ?? false,
            enable_incident_reporting: companyResult?.data?.enable_incident_reporting ?? true,
          };
          setCompanySettings(settings);

          // Check pending toolbox talks if sign-off required
          if (settings?.require_toolbox_talk_signoff) {
            const talksResult = await toolboxTalksService?.getTalksForWorker(user?.id);
            if (talksResult?.error) {
              // Gate-check failure — log so it's diagnosable, but don't block
              // the worker home from rendering. Falls open: if we can't read
              // assignments, we don't enforce the gate.
              console.error('[WorkerHome] toolbox gate check failed:', { code: talksResult?.error?.code, message: talksResult?.error?.message });
            } else if (talksResult?.data?.length > 0) {
              const statusChecks = await Promise.all(
                talksResult?.data?.map(a =>
                  toolboxTalksService?.getWorkerConfirmationStatus(a?.talk_id, user?.id)
                )
              );
              const pending = statusChecks?.filter(r => !r?.data)?.length;
              setPendingToolboxTalks(pending);
              setToolboxBlocked(pending > 0);
            }
          }
        }

        const recordResult = await dailyRecordService?.getOrCreateDailyRecord(
          user?.id,
          assignmentResult?.data?.site_id,
          companyResult?.data?.id,
          today
        );
        if (recordResult?.error) throw recordResult?.error;
        setDailyRecordId(recordResult?.data);
      }
    } catch (err) {
      console.error('[WorkerHome] load home failed:', err);
      setError('Failed to load your home page. Please refresh.');
    } finally {
      setLoading(false);
    }
  };

  const handleNothingToReport = async () => {
    if (assignedSiteReadOnly) {
      setError('This site is read-only. Updates cannot be saved.');
      return;
    }
    if (!dailyRecordId) {
      setError('No daily record found. Please contact your site admin.');
      return;
    }
    try {
      setError('');
      const result = await dailyRecordService?.markNothingToReport(dailyRecordId);
      if (result?.error) throw result?.error;
      setSuccessMessage('Nothing to report recorded successfully');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('[WorkerHome] nothing to report failed:', err);
      // Pass-through sanitised detail so users (and Heidi during testing)
      // can self-diagnose schema/RLS errors instead of seeing the same
      // opaque message that masked the nothing_to_report_timestamp bug.
      const detail = sanitizeError(err?.message, 'Please try again.');
      setError(`Failed to record your update: ${detail}`);
    }
  };

  const handleAddPhoto = () => {
    if (assignedSiteReadOnly) {
      setError('This site is read-only. Photos cannot be uploaded.');
      return;
    }
    if (!dailyRecordId) {
      setError('No daily record found. Please contact your site admin.');
      return;
    }
    navigate('/camera-first-photo-upload', { state: { dailyRecordId, siteName: todayAssignment?.site_name } });
  };

  const handleCompleteTask = () => {
    if (!todayAssignment) {
      setError('You have no site assignment for today.');
      return;
    }
    navigate(`/tasks`);
  };

  const handleDismissOnboarding = () => {
    const key = `workerOnboardingSeen_${user?.id}`;
    localStorage.setItem(key, 'true');
    setShowOnboarding(false);
  };

  const firstName = userProfile?.full_name?.split(' ')?.[0] || '';

  // `phone` is empty by default on invite and only gets populated when the
  // worker actively edits profile-settings, so a non-empty phone is our
  // best available signal that the "Complete your profile" nudge is done.
  const profileLooksComplete = !!userProfile?.phone?.trim();

  const onboardingCards = [
    !profileLooksComplete && {
      icon: 'UserCircle',
      label: t('onboardingCompleteProfile') || 'Complete your profile',
      onClick: () => navigate('/profile-settings'),
      color: 'bg-blue-500',
    },
    {
      icon: 'ClipboardList',
      label: t('onboardingViewAssignments') || 'View your assignments',
      onClick: () => navigate('/task-completion-workflow'),
      color: 'bg-teal-500',
    },
    {
      icon: 'CheckSquare',
      label: t('onboardingSubmitCheckin') || 'Submit your first check-in',
      onClick: () => navigate('/daily-site-checklist'),
      color: 'bg-cyan-500',
    },
  ].filter(Boolean);

  const completedCount = todayTasks?.filter(t => t?.status === 'completed')?.length;
  const totalCount = todayTasks?.length;
  const progressPct = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center h-[calc(100vh-4rem)] pt-16">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent"></div>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Today - SiteProof</title>
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />
        <main className="pt-16">
          <div className="max-w-2xl mx-auto px-3 sm:px-4 py-6">

            <SubscriptionReadOnlyBanner className="mb-6" />

            {/* First-time Worker Onboarding Banner */}
            {showOnboarding && (
              <div className="mb-6 rounded-2xl overflow-hidden shadow-md bg-gradient-to-br from-blue-600 via-blue-500 to-teal-500 text-white">
                <div className="p-4 sm:p-5">
                  <h2 className="text-lg sm:text-xl font-bold mb-1">
                    {(t('onboardingWelcome') || 'Welcome to SiteProof, {name}!')?.replace('{name}', firstName)}
                  </h2>
                  <p className="text-sm text-blue-100 mb-4">
                    {t('onboardingSubtitle') || "Here's how to get started."}
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-3 mb-4">
                    {onboardingCards?.map((card) => (
                      <button
                        key={card?.label}
                        onClick={card?.onClick}
                        className="flex flex-row sm:flex-col items-center gap-3 sm:gap-2 bg-white/15 hover:bg-white/25 active:bg-white/30 rounded-xl p-3 transition-colors text-left sm:text-center min-h-[56px]"
                      >
                        <div className={`w-10 h-10 rounded-full ${card?.color} flex items-center justify-center shadow-sm shrink-0`}>
                          <Icon name={card?.icon} size={20} className="text-white" />
                        </div>
                        <span className="text-sm sm:text-xs font-medium text-white leading-tight">{card?.label}</span>
                      </button>
                    ))}
                  </div>
                  <button
                    onClick={handleDismissOnboarding}
                    className="w-full bg-white text-blue-600 font-semibold text-sm py-3 rounded-xl hover:bg-blue-50 active:bg-blue-100 transition-colors min-h-[48px]"
                  >
                    {t('onboardingDismiss') || "Got it, let's get started"}
                  </button>
                </div>
              </div>
            )}

            {/* Greeting + Site Name */}
            <div className="mb-6">
              <h1 className="text-2xl font-bold text-foreground mb-1">{greeting}</h1>
              {todayAssignment ? (
                <>
                  <div className="flex items-center gap-2 text-accent">
                    <Icon name="MapPin" size={16} />
                    <span className="text-sm font-semibold">{todayAssignment?.site_name}</span>
                    {todayAssignment?.site_address && (
                      <span className="text-xs text-muted-foreground">· {todayAssignment?.site_address}</span>
                    )}
                  </div>
                  {othersOnSite?.length > 0 && (
                    <p className="text-xs text-muted-foreground mt-1 ml-6">
                      with {(() => {
                        const MAX = 3;
                        const names = othersOnSite?.map(p => p?.full_name)?.filter(Boolean);
                        if (names?.length <= MAX) {
                          if (names.length === 1) return names[0];
                          if (names.length === 2) return `${names[0]} and ${names[1]}`;
                          return `${names.slice(0, -1).join(', ')}, and ${names[names.length - 1]}`;
                        }
                        const shown = names.slice(0, MAX);
                        const extra = names.length - MAX;
                        return `${shown.join(', ')} +${extra} more`;
                      })()}
                    </p>
                  )}
                </>
              ) : (
                <p className="text-sm text-muted-foreground">No site assigned today</p>
              )}
            </div>

            <SiteReadOnlyBanner status={assignedSiteStatus} siteId={todayAssignment?.site_id} className="mb-4" />

            {/* Error / Success */}
            {error && (
              <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4 mb-4 flex items-start gap-3">
                <Icon name="AlertCircle" size={18} className="text-destructive mt-0.5 shrink-0" />
                <p className="text-sm text-destructive">{error}</p>
              </div>
            )}
            {successMessage && (
              <div className="bg-success/10 border border-success/20 rounded-lg p-4 mb-4 flex items-start gap-3">
                <Icon name="CheckCircle" size={18} className="text-success mt-0.5 shrink-0" />
                <p className="text-sm text-success">{successMessage}</p>
              </div>
            )}

            {/* My Schedule */}
            <div className="bg-card border border-border rounded-xl p-5 mb-5 shadow-sm">
              <div className="flex items-center gap-2 mb-4">
                <Icon name="CalendarDays" size={20} className="text-accent" />
                <h2 className="text-base font-semibold text-foreground">My Schedule</h2>
              </div>
              {upcomingAssignments?.length === 0 ? (
                <div className="text-center py-4">
                  <Icon name="Calendar" size={28} className="text-muted-foreground mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground">No upcoming assignments. Contact your admin.</p>
                </div>
              ) : (
                <ul className="space-y-2">
                  {upcomingAssignments?.map((a, idx) => {
                    const assignDate = new Date(a?.assignment_date + 'T00:00:00');
                    const todayDate = new Date();
                    todayDate?.setHours(0, 0, 0, 0);
                    const diffDays = Math.round((assignDate - todayDate) / (1000 * 60 * 60 * 24));
                    const dayLabel = diffDays === 0 ? 'Today' : diffDays === 1 ? 'Tomorrow' : assignDate?.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
                    const isToday = diffDays === 0;
                    return (
                      <li key={a?.assignment_id || idx} className={`flex items-center gap-3 p-3 rounded-lg ${isToday ? 'bg-accent/10 border border-accent/20' : 'bg-muted/40'}`}>
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${isToday ? 'bg-accent text-white' : 'bg-muted text-muted-foreground'}`}>
                          <Icon name={isToday ? 'MapPin' : 'Calendar'} size={15} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm font-medium truncate ${isToday ? 'text-accent' : 'text-foreground'}`}>
                            {a?.site_name}
                          </p>
                          <p className="text-xs text-muted-foreground">{dayLabel}</p>
                        </div>
                        {isToday && (
                          <span className="text-xs font-semibold text-accent bg-accent/10 px-2 py-0.5 rounded-full">Today</span>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            {/* Today's Tasks */}
            <div className="bg-card border border-border rounded-xl p-5 mb-5 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Icon name="ClipboardList" size={20} className="text-accent" />
                  <h2 className="text-base font-semibold text-foreground">My Tasks</h2>
                </div>
                {totalCount > 0 && (
                  <span className="text-xs font-medium text-muted-foreground bg-muted px-2 py-1 rounded-full">
                    {completedCount}/{totalCount} done
                  </span>
                )}
              </div>

              {totalCount > 0 && (
                <div className="mb-4">
                  <div className="flex justify-between text-xs text-muted-foreground mb-1">
                    <span>Progress</span>
                    <span>{progressPct}%</span>
                  </div>
                  <div className="w-full bg-muted rounded-full h-2">
                    <div
                      className="bg-accent h-2 rounded-full transition-all duration-300"
                      style={{ width: `${progressPct}%` }}
                    />
                  </div>
                </div>
              )}

              {totalCount === 0 ? (
                <div className="text-center py-6">
                  <Icon name="CheckCircle2" size={32} className="text-muted-foreground mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground">
                    {todayAssignment ? 'No tasks assigned for today' : 'No site assigned — contact your admin'}
                  </p>
                </div>
              ) : (
                <ul className="space-y-2">
                  {todayTasks?.slice(0, 5)?.map(task => (
                    <li
                      key={task?.id}
                      className="flex items-center gap-3 p-3 rounded-lg bg-muted/40 hover:bg-muted/70 cursor-pointer transition-colors"
                      onClick={() => task?.status === 'completed' ? setDetailTask(task) : navigate('/tasks')}
                    >
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 ${
                        task?.status === 'completed'
                          ? 'bg-success border-success' :'border-muted-foreground'
                      }`}>
                        {task?.status === 'completed' && (
                          <Icon name="Check" size={12} className="text-white" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-medium truncate ${
                          task?.status === 'completed' ? 'line-through text-muted-foreground' : 'text-foreground'
                        }`}>
                          {task?.title}
                        </p>
                        {task?.status === 'completed' && task?.completed_at ? (
                          <p className="text-xs text-success">{new Date(task?.completed_at)?.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}</p>
                        ) : task?.due_time ? (
                          <p className="text-xs text-muted-foreground">{task?.due_time}</p>
                        ) : null}
                      </div>
                      {task?.photo_required && task?.status !== 'completed' && (
                        <Icon name="Camera" size={14} className="text-amber-500 shrink-0" />
                      )}
                      {task?.status === 'completed' && (
                        <Icon name="ChevronRight" size={14} className="text-muted-foreground shrink-0" />
                      )}
                    </li>
                  ))}
                  {totalCount > 5 && (
                    <li
                      className="text-center text-xs text-accent cursor-pointer py-1 hover:underline"
                      onClick={() => navigate('/tasks')}
                    >
                      View all {totalCount} tasks →
                    </li>
                  )}
                </ul>
              )}
            </div>

            {/* Quick Actions */}
            <div className="bg-card border border-border rounded-xl p-5 shadow-sm">
              <div className="flex items-center gap-2 mb-4">
                <Icon name="Zap" size={20} className="text-accent" />
                <h2 className="text-base font-semibold text-foreground">Quick Actions</h2>
              </div>

              {toolboxBlocked && (
                <div className="mb-4 flex items-start gap-3 p-3 bg-amber-50 border border-amber-200 rounded-xl">
                  <Icon name="AlertTriangle" size={18} className="text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-semibold text-amber-800">Toolbox talk sign-off required</p>
                    <p className="text-xs text-amber-700 mt-0.5">
                      You have {pendingToolboxTalks} pending toolbox talk{pendingToolboxTalks !== 1 ? 's' : ''} to sign off before starting tasks.
                    </p>
                    <button
                      onClick={() => navigate('/toolbox-talks-worker-view')}
                      className="mt-2 text-xs font-semibold text-amber-800 underline"
                    >
                      Go to Toolbox Talks →
                    </button>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 gap-3">
                <Button
                  onClick={handleAddPhoto}
                  disabled={!todayAssignment || assignedSiteReadOnly}
                  className="w-full h-14 text-base bg-accent hover:bg-accent/90 text-white justify-start px-5 gap-3"
                >
                  <Icon name="Camera" size={22} />
                  <span>Add Photo</span>
                </Button>

                <Button
                  onClick={toolboxBlocked ? () => navigate('/toolbox-talks-worker-view') : handleCompleteTask}
                  disabled={!todayAssignment}
                  className={`w-full h-14 text-base text-white justify-start px-5 gap-3 ${toolboxBlocked ? 'bg-muted-foreground hover:bg-muted-foreground/90 cursor-not-allowed' : 'bg-success hover:bg-success/90'}`}
                  title={toolboxBlocked ? 'Complete toolbox talks first' : ''}
                >
                  <Icon name="CheckSquare" size={22} />
                  <span>My Tasks{toolboxBlocked ? ' (Toolbox required)' : ''}</span>
                </Button>

                <Button
                  onClick={() => navigate('/snags-create')}
                  disabled={assignedSiteReadOnly}
                  className="w-full h-14 text-base bg-amber-500 hover:bg-amber-600 text-white justify-start px-5 gap-3"
                >
                  <Icon name="AlertTriangle" size={22} />
                  <span>Report Issue</span>
                </Button>

                {companySettings?.enable_incident_reporting && (
                  <Button
                    onClick={() => navigate('/incident-reporting')}
                    disabled={assignedSiteReadOnly}
                    className="w-full h-14 text-base bg-red-600 hover:bg-red-700 text-white justify-start px-5 gap-3"
                  >
                    <Icon name="ShieldAlert" size={22} />
                    <span>Report Incident</span>
                  </Button>
                )}

                <Button
                  onClick={() => navigate('/rams-worker-view')}
                  className="w-full h-14 text-base bg-slate-600 hover:bg-slate-700 text-white justify-start px-5 gap-3"
                >
                  <Icon name="FileCheck" size={22} />
                  <span>My H&S Docs</span>
                </Button>

                <Button
                  onClick={() => navigate('/site-inductions-worker-view')}
                  className="w-full h-14 text-base bg-teal-600 hover:bg-teal-700 text-white justify-start px-5 gap-3"
                >
                  <Icon name="GraduationCap" size={22} />
                  <span>My Inductions</span>
                </Button>

                <Button
                  onClick={() => navigate('/toolbox-talks-worker-view')}
                  className={`w-full h-14 text-base text-white justify-start px-5 gap-3 ${pendingToolboxTalks > 0 ? 'bg-amber-600 hover:bg-amber-700' : 'bg-violet-600 hover:bg-violet-700'}`}
                >
                  <Icon name="MessageSquare" size={22} />
                  <span>Toolbox Talks{pendingToolboxTalks > 0 ? ` (${pendingToolboxTalks} pending)` : ''}</span>
                </Button>
              </div>
            </div>

            {!todayAssignment && (
              <div className="mt-5 bg-muted/30 border border-border rounded-xl p-5">
                <div className="flex items-start gap-3">
                  <Icon name="Info" size={18} className="text-accent mt-0.5 shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-foreground mb-1">No Assignment Today</p>
                    <p className="text-sm text-muted-foreground">
                      You need to be assigned to a site before you can use these features.
                      Please contact your site administrator.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </main>
      </div>
      {detailTask && (
        <TaskDetailModal task={detailTask} onClose={() => setDetailTask(null)} />
      )}
    </>
  );
};

export default WorkerHome;